import type {
  WalletLookupResponse, WalletStats, WalletTag, WalletCategory,
  ScreenResult, ScreenBatchResult, ExposureResult, ClusterResult,
  SarExportResponse, CaseExhibitsResponse, CaseReportSectionResponse,
  AttributionResult, TransactionsResponse, CategorySampleResponse,
  AuditLogResponse, WalletIntelSettings,
} from '@/types/walletIntel';
import { apiRequest as req } from '@/lib/apiRequest';

// No /v1 prefix — the wallet-intel router is mounted directly at /wallet-intel
// (apps/api/routers/wallet_intel.py: APIRouter(prefix="/wallet-intel")).
const BASE = '/wallet-intel';

export interface AddEntryInput {
  chain: string;
  address: string;
  category: WalletCategory;
  citation: string;
  source_date: string;
  case_id?: string;
}

export interface ScreenInput {
  chain: string;
  address: string;
  include_exposure?: boolean;
  max_hops?: number;
  max_fanout?: number;
}

export interface ScreenBatchInput {
  items: { chain: string; address: string }[];
  include_exposure?: boolean;
  max_hops?: number;
  max_fanout?: number;
}

export interface ExposureInput {
  chain: string;
  address: string;
  max_hops?: number;
  max_fanout?: number;
}

export interface ClusterInput {
  chain: string;
  address: string;
  max_hops?: number;
  max_fanout?: number;
}

export interface SarExportInput {
  chain: string;
  address: string;
  reporting_entity_name: string;
  filed_by: string;
  case_id?: string;
  narrative?: string;
  include_exposure?: boolean;
}

export interface TransactionsInput {
  chain: string;
  address: string;
  cursor?: string;
  filter_direction?: 'Incoming' | 'Outgoing';
}

export interface AuditLogInput {
  action?: string;
  chain?: string;
  address?: string;
  case_id?: string;
  user_id?: string; // admin only — ignored server-side otherwise
  limit?: number;
  before?: string;
}

export type SettingsInput = Partial<Omit<WalletIntelSettings, 'user_id' | 'updated_at'>>;

export const walletIntelApi = {
  lookup: (chain: string, address: string) =>
    req<WalletLookupResponse>(
      `${BASE}/lookup?chain=${encodeURIComponent(chain)}&address=${encodeURIComponent(address)}`,
    ),

  stats: () => req<WalletStats>(`${BASE}/stats`),

  addEntry: (body: AddEntryInput) =>
    req<WalletTag & { entity_id?: string; case_link_warning?: string }>(`${BASE}/entries`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  screen: (body: ScreenInput) =>
    req<ScreenResult>(`${BASE}/screen`, { method: 'POST', body: JSON.stringify(body) }),

  screenBatch: (body: ScreenBatchInput) =>
    req<ScreenBatchResult>(`${BASE}/screen-batch`, { method: 'POST', body: JSON.stringify(body) }),

  exposure: (body: ExposureInput) =>
    req<ExposureResult>(`${BASE}/exposure`, { method: 'POST', body: JSON.stringify(body) }),

  cluster: (body: ClusterInput) =>
    req<ClusterResult>(`${BASE}/cluster`, { method: 'POST', body: JSON.stringify(body) }),

  sarExport: (body: SarExportInput) =>
    req<SarExportResponse>(`${BASE}/sar-export`, { method: 'POST', body: JSON.stringify(body) }),

  caseExhibits: (caseId: string) => req<CaseExhibitsResponse>(`${BASE}/case/${caseId}/exhibits`),

  caseReportSection: (caseId: string) =>
    req<CaseReportSectionResponse>(`${BASE}/case/${caseId}/report-section`),

  attribution: (chain: string, address: string) =>
    req<AttributionResult>(
      `${BASE}/breadcrumbs/attribution?chain=${encodeURIComponent(chain)}&address=${encodeURIComponent(address)}`,
    ),

  transactions: (body: TransactionsInput) =>
    req<TransactionsResponse>(`${BASE}/breadcrumbs/transactions`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  categorySample: (category: string, limit = 8) =>
    req<CategorySampleResponse>(`${BASE}/stats/category/${encodeURIComponent(category)}/sample?limit=${limit}`),

  auditLog: (params: AuditLogInput = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== '') qs.set(k, String(v));
    });
    const q = qs.toString();
    return req<AuditLogResponse>(`${BASE}/audit-log${q ? `?${q}` : ''}`);
  },

  getSettings: () => req<WalletIntelSettings>(`${BASE}/settings`),

  updateSettings: (body: SettingsInput) =>
    req<WalletIntelSettings>(`${BASE}/settings`, { method: 'PUT', body: JSON.stringify(body) }),
};
